import type { ICandidate } from "@bbj-types/candidate";
import { CommunicationType } from "../enums/communication";

type TCandidateCommunicationFields = Pick<ICandidate, "optInEmail" | "optInPhone" | "phone" | "mail" | "isEmailValid">;

/**
 * A channel is usable only when the matching contact detail is filled in on the candidate profile,
 * on top of the contact opt-in: candidate-serverless applies the same rule before sending.
 */
const canBeReachedBySms = (candidate: TCandidateCommunicationFields) =>
  Boolean(candidate.phone && candidate.optInPhone);

const canBeReachedByEmail = (candidate: TCandidateCommunicationFields) =>
  Boolean(candidate.mail && candidate.isEmailValid && candidate.optInEmail);

export const getCandidateCommunicationPreferences = (
  candidate?: TCandidateCommunicationFields & Pick<ICandidate, "communicationType">
): CommunicationType | null => {
  if (!candidate) {
    return null;
  }
  switch (candidate.communicationType) {
    case CommunicationType.SMS:
    case CommunicationType.CALL:
      if (canBeReachedBySms(candidate)) {
        return CommunicationType.SMS;
      }
      break;
    case CommunicationType.EMAIL:
      if (canBeReachedByEmail(candidate)) {
        return CommunicationType.EMAIL;
      }
      break;
  }
  if (canBeReachedBySms(candidate)) {
    return CommunicationType.SMS;
  }
  if (canBeReachedByEmail(candidate)) {
    return CommunicationType.EMAIL;
  }
  return null;
};

export const getCandidateAvailableCommunications = (candidate?: TCandidateCommunicationFields) => {
  const availableCommunications: Set<CommunicationType> = new Set();
  if (!candidate) {
    return availableCommunications;
  }
  if (canBeReachedBySms(candidate)) {
    availableCommunications.add(CommunicationType.SMS);
  }
  if (canBeReachedByEmail(candidate)) {
    availableCommunications.add(CommunicationType.EMAIL);
  }
  return availableCommunications;
};

export const getCandidateMissingContactDetails = (candidate?: TCandidateCommunicationFields) => {
  const missing: string[] = [];
  if (!candidate) {
    return missing;
  }
  if (!candidate.mail) {
    missing.push("Adresse mail");
  }
  if (!candidate.phone) {
    missing.push("Téléphone");
  }
  return missing;
};
